import { combineReducers, configureStore } from "@reduxjs/toolkit";
import {
	FLUSH,
	PAUSE,
	PERSIST,
	PURGE,
	persistReducer,
	persistStore,
	REGISTER,
	REHYDRATE,
} from "redux-persist";
import {
	createStateSyncMiddleware,
	initMessageListener,
} from "redux-state-sync";
import bearerReducer from "../slice/bearerSlice";
import conversationReducer from "../slice/ConversationSlice";
import deviceReducer from "../slice/deviceSlice";
import errorModalValReducer from "../slice/errModalValidationSlice";
import inputMessageReducer from "../slice/InputMessageSlice";
import loaderReducer from "../slice/loaderSlice";
import messagesReducer from "../slice/MessageSlice";
import visibilityReducer from "../slice/ModalVisibilitySlice";
import messageProfileValReducer from "../slice/messageProfileValidationSlice";
import passwordReducer from "../slice/passwordSlice";
import historyReducer from "../slice/profileHistorySlice";
import settingReducer from "../slice/SettingSlice";
import sideMenureducer from "../slice/SideMenuSlice";
import topicStarsReducer from "../slice/topicRatingSlice";
import unloggedUserReducer from "../slice/unloggedUserSlice";
import userActionsReducer from "../slice/userActionsSlice";
import dataReducer from "../slice/userDataSlice";
import variantValidatorReducer from "../slice/VariantValidatorSlice";

const createStorage = (storageName) => ({
	getItem: (key) => {
		try {
			return Promise.resolve(
				typeof window === "undefined" ? null : window[storageName].getItem(key),
			);
		} catch {
			return Promise.resolve(null);
		}
	},
	setItem: (key, value) => {
		try {
			if (typeof window !== "undefined") {
				window[storageName].setItem(key, value);
			}
		} catch {
			// Persistence is optional when browser storage is unavailable.
		}
		return Promise.resolve();
	},
	removeItem: (key) => {
		try {
			if (typeof window !== "undefined") {
				window[storageName].removeItem(key);
			}
		} catch {
			// Persistence is optional when browser storage is unavailable.
		}
		return Promise.resolve();
	},
});

const storage = createStorage("localStorage");
const storageSession = createStorage("sessionStorage");

const interruptPendingMessages = (state) => {
	const messages = state.messages?.message;
	if (!Array.isArray(messages)) {
		return state;
	}

	let hasPendingMessages = false;
	const interruptedMessages = messages.map((message) => {
		const interruptionText = message.pending?.interruptionText;

		if (!interruptionText) {
			return message;
		}

		hasPendingMessages = true;
		return {
			...message,
			text: message.pending.clearTextOnInterrupt ? "" : message.text,
			stopText: interruptionText,
			pending: null,
		};
	});

	if (!hasPendingMessages) {
		return state;
	}

	return {
		...state,
		messages: {
			...state.messages,
			message: interruptedMessages,
		},
	};
};

const migrateRootState = (state) => {
	if (!state || typeof state !== "object") {
		return Promise.resolve(state);
	}

	const { variantValidator: _persistedVariantValidator, ...rootState } = state;
	const migratedRootState = interruptPendingMessages(rootState);
	if (
		!migratedRootState.bearer ||
		typeof migratedRootState.bearer !== "object"
	) {
		return Promise.resolve(migratedRootState);
	}

	const { controller: _legacyController, ...bearer } = migratedRootState.bearer;
	return Promise.resolve({ ...migratedRootState, bearer });
};

const migrateBearerState = (state) => {
	if (!state || typeof state !== "object") {
		return Promise.resolve(state);
	}

	const { controller: _legacyController, ...bearer } = state;
	return Promise.resolve(bearer);
};

const persistConfig = {
	key: "root",
	storage: storageSession,
	version: 1,
	migrate: migrateRootState,
	blacklist: [
		"settings",
		"data",
		"bearer",
		"useractions",
		"unlogged",
		"variantValidator",
	],
};

const persistConfigToken = {
	key: "bearer",
	storage,
	version: 1,
	migrate: migrateBearerState,
	whitelist: ["value", "refreshToken"],
};

const persistConfigUser = {
	key: "user",
	storage,
	whitelist: ["value"],
};

// const filter = createFilter(
//   `value.language`
// );

const persistConfigLanguage = {
	key: "settings",
	storage,
	whitelist: ["language"],
};

const persistUserSearchCounter = {
	key: "useractions",
	storage,
	whitelist: ["request"],
};

const persistUnloggedUserSearchCounter = {
	key: "unlogged",
	storage,
	whitelist: ["queryDoneCounter", "counterInitTime"],
};

const persistedReducer = combineReducers({
	messages: messagesReducer,
	sidemenu: sideMenureducer,
	inputmessage: inputMessageReducer,
	settings: persistReducer(persistConfigLanguage, settingReducer),
	visibility: visibilityReducer,
	variantValidator: variantValidatorReducer,
	loader: loaderReducer,
	device: deviceReducer,
	password: passwordReducer,
	data: persistReducer(persistConfigUser, dataReducer),
	bearer: persistReducer(persistConfigToken, bearerReducer),
	errorModalVal: errorModalValReducer,
	messageProfileVal: messageProfileValReducer,
	useractions: persistReducer(persistUserSearchCounter, userActionsReducer),
	history: historyReducer,
	conversation: conversationReducer,
	unlogged: persistReducer(
		persistUnloggedUserSearchCounter,
		unloggedUserReducer,
	),
	topicstars: topicStarsReducer,
});

const allPersistedReducer = persistReducer(persistConfig, persistedReducer);

export const store = configureStore({
	reducer: allPersistedReducer,
	middleware: (getDefaultMiddleware) => {
		const middleware = getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
			},
		});

		if (typeof window === "undefined") {
			return middleware;
		}

		return middleware.concat(
			createStateSyncMiddleware({
				whitelist: [
					"bearer/setBearer",
					"bearer/setRefreshToken",
					"data/setUserData",
					"data/resetUserData",
					"settings/setLanguage",
					"settings/resetLanguage",
					"useractions/request",
					"unlogged/queryDoneCounter",
					"unlogged/counterInitTime",
				],
			}),
		);
	},
});

if (typeof window !== "undefined") {
	initMessageListener(store);
}

export const persistor = persistStore(store);
